import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Modal, Skeleton, Tabs, message } from "antd";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import ApplyBrandIcon from "../../../icons/ApplyBrandIcon";
import { brandKitsState, memberState } from "../../../../state/atoms";
import BrandKitApi from "../../../../api/BrandKitApi";
import { env } from "../../../../env/env";
import { AssetsTab } from "./tabs/AssetsTab";
import { ColorsTab } from "./tabs/ColorsTab";
import { FontsTab } from "./tabs/FontsTab";
import { IconsTab } from "./tabs/IconsTab";
import { TemplatesTab } from "./tabs/TemplatesTab";
import { AssignBrandToLocationModal } from "./AssignBrandToLocationModal";
import WarningNotice from "../../../WarningNotice";
import { AppContext } from "../../../../AppContext";
import { EditableTitle } from "../../../EditableTitle";
import "./BrandManagementDetailPage.scss";

export function BrandManagementDetailPage() {
  const navigate = useNavigate();
  const { brandKitId } = useParams<{ brandKitId: string }>();
  const member = useRecoilValue(memberState);
  const teamId = member?.teamId || "";

  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);
  const [loading, setLoading] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "assets");

  const canManageBrand = !!member && (
    member.owner || member.role === "Admin" || (member as any).manageBrand
  );

  // The Icons tab (DRD attribute -> icon mapping) only applies to accounts with a DiningRD integration.
  const hasDrdIntegration = !!(member as any)?.diningRDIntegration;

  useEffect(() => {
    if (member && !canManageBrand) {
      navigate(`${env.dashboardPath}/account`, { replace: true });
    }
  }, [member, canManageBrand, navigate]);

  useEffect(() => {
    if (!teamId || brandKits.length > 0) return;
    setLoading(true);
    BrandKitApi.listForTeam(teamId)
      .then((resp) => setBrandKits(resp.data || []))
      .catch(() => message.error("Failed to load brand kits"))
      .finally(() => setLoading(false));
  }, [teamId, brandKits.length, setBrandKits]);

  const { location } = useContext(AppContext);

  const selected = useMemo(
    () => brandKits.find((b) => b.id === brandKitId),
    [brandKits, brandKitId]
  );

  // A brand template is a design entry that must carry a location id. The brand kit can be
  // assigned to several locations, so prefer the active location when it belongs to the kit,
  // otherwise fall back to the kit's first location.
  const templateLocationId = useMemo(() => {
    const ids = selected?.locationIds || [];
    const active = location?.internalId;
    if (active != null && ids.includes(active)) {
      return active;
    }
    return ids[0];
  }, [selected, location]);

  const applyAssetCountDelta = (delta: number) => {
    if (!brandKitId || delta === 0) return;
    setBrandKits((prev) =>
      prev.map((b) =>
        b.id === brandKitId
          ? ({ ...b, assetCount: Math.max(0, ((b as any).assetCount ?? 0) + delta) } as any)
          : b
      )
    );
  };

  const renameBrandKit = (name: string) => {
    if (!selected?.id) return Promise.resolve();
    return BrandKitApi.update(selected.id, { ...selected, name })
      .then((resp) => {
        setBrandKits(
          brandKits.map((b) => (b.id === resp.data.id ? resp.data : b))
        );
        message.success("Brand kit renamed");
      })
      .catch(() => message.error("Failed to rename brand kit"));
  };

  const openAssignModal = () => {
    if (!selected) return;
    setAssignOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!selected?.id) return;
    setDeleting(true);
    BrandKitApi.delete(selected.id)
      .then(() => {
        setBrandKits(brandKits.filter((b) => b.id !== selected.id));
        setDeleteOpen(false);
        message.success("Brand kit deleted");
        navigate(`${env.dashboardPath}/account/brand-management`);
      })
      .catch(() => message.error("Failed to delete brand kit"))
      .finally(() => setDeleting(false));
  };

  if (loading && !selected) {
    return (
      <div className="brand-management-detail-page">
        <Skeleton active />
      </div>
    );
  }

  if (!loading && !selected) {
    return (
      <div className="brand-management-detail-page">
        <div className="brand-management-detail-missing">
          <p>Brand kit not found.</p>
          <Button
            type="primary"
            onClick={() =>
              navigate(`${env.dashboardPath}/account/brand-management`)
            }
          >
            Back to Brand Management
          </Button>
        </div>
      </div>
    );
  }

  const locCount = (selected?.locationIds || []).length;
  const assetCount = (selected as any)?.assetCount;
  const templateCount = (selected as any)?.templateCount;
  const assetLabel = typeof assetCount === "number" ? assetCount : "—";
  const templateLabel = typeof templateCount === "number" ? templateCount : "—";
  const subtitleParts = [
    `${locCount} ${locCount === 1 ? "location" : "locations"}`,
    `${assetLabel} ${assetLabel === 1 ? "asset" : "assets"}`,
    `${templateLabel} ${templateLabel === 1 ? "template" : "templates"}`,
  ];

  return (
    <div className="brand-management-detail-page">
      {locCount === 0 && (
        <WarningNotice
          text="This brand kit isn't assigned to any locations yet. Brand templates won't be available in the editor until you assign it."
          action={
            <span className="link" onClick={openAssignModal}>
              Assign Locations
            </span>
          }
        />
      )}

      <nav className="brand-management-breadcrumb">
        <Link to={`${env.dashboardPath}/account/brand-management`}>
          Brand Management
        </Link>
        <span className="brand-management-breadcrumb-sep">&gt;</span>
        <span className="brand-management-breadcrumb-current">
          {selected?.name || "Untitled"}
        </span>
      </nav>

      <header className="brand-management-detail-header">
        <div className="brand-management-detail-header-left">
          <EditableTitle value={selected?.name || ""} onSave={renameBrandKit} />
          <p className="brand-management-detail-subtitle">
            {subtitleParts.join(" · ")}
          </p>
        </div>

        <div className="brand-management-detail-header-right">
          <Button
            className="brand-management-action-btn assign secondary"
            icon={<ApplyBrandIcon />}
            onClick={openAssignModal}
          >
            Assign Brand to Location
          </Button>
        </div>
      </header>

      <Tabs
        className="brand-management-tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "assets",
            label: "Uploads",
            children: selected && (
              <AssetsTab
                brandKitId={selected.id!}
                brandKitName={selected.name}
                onCountDelta={applyAssetCountDelta}
              />
            ),
          },
          {
            key: "colors",
            label: "Colors",
            children: selected && <ColorsTab selected={selected} />,
          },
          {
            key: "fonts",
            label: "Fonts",
            children: selected && <FontsTab selected={selected} />,
          },
          ...(hasDrdIntegration
            ? [
                {
                  key: "icons",
                  label: "Icon Attributes",
                  children: selected && <IconsTab brandKitId={selected.id!} />,
                },
              ]
            : []),
          {
            key: "templates",
            label: "Brand Templates",
            children: selected && (
              <TemplatesTab
                brandKitId={selected.id!}
                locationId={templateLocationId}
              />
            ),
          },
        ]}
      />

      <AssignBrandToLocationModal
        open={assignOpen}
        brandKit={selected || null}
        onClose={() => setAssignOpen(false)}
      />

      <Modal
        className={"clean one"}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        footer={null}
      >
        <header>
          <h2>Are you sure?</h2>
        </header>

        <p>This brand will be removed and all its data will be lost.</p>

        <footer>
          <Button size={"large"} onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            size={"large"}
            className={"primary"}
            onClick={handleConfirmDelete}
            loading={deleting}
          >
            Delete
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
